// Field progress rolled up by work package or by coordination area.
//
// The field's lane only (owner's rule, 2026-09-17: issues track BIM,
// construction tracking tracks the field, checklists track management).
// Every number here is COUNTED from tracked model elements by the server
// (GET /field-tracking/rollup); nothing is computed or reconciled in the
// browser, including the typed-vs-counted mismatch, which the server
// computes once for every page that shows it.
//
// A group with no elements is not "0% done", it is unmeasured, so it is
// listed apart and never drawn as an empty bar.
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Progress, Segmented, Skeleton, Table, Tag, Tooltip, Typography } from "antd";
import { url, tokenRef } from "api";

export async function fetchRollup(projectId, by) {
  const r = await fetch(`${url}/projects/${projectId}/field-tracking/rollup?by=${by}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
  return body;
}

export default function FieldProgressRollup({ projectId }) {
  const [by, setBy] = useState("package");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showUnmeasured, setShowUnmeasured] = useState(false);

  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    setData(null); setErr("");
    fetchRollup(projectId, by)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message || "Could not read the field progress"); });
    return () => { alive = false; };
  }, [projectId, by]);

  const groups = (data && data.groups) || [];
  const measured = groups.filter((g) => g.tracked);
  const unmeasured = groups.filter((g) => !g.tracked);

  const columns = [
    {
      title: by === "area" ? "Area" : "Package",
      dataIndex: "label",
      render: (v, g) => (
        <span>
          {g.color ? <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: g.color, marginRight: 6, verticalAlign: -1 }} /> : null}
          <b>{v}</b>
          <span style={{ color: "#888" }}> · {g.activities} activit{g.activities === 1 ? "y" : "ies"}</span>
        </span>
      ),
    },
    { title: "Elements", dataIndex: "total", width: 90, align: "right" },
    {
      title: "Installed",
      dataIndex: "percentInstalled",
      width: 200,
      render: (v, g) => (
        <Tooltip title={`${(g.byStatus && g.byStatus.installed) || 0} of ${g.total} installed`}>
          <Progress percent={v} size="small" status={v >= 100 ? "success" : "active"} />
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Work under way counts for part of it, with this project's weights"><span>Progress</span></Tooltip>,
      dataIndex: "percentWeighted",
      width: 100,
      align: "right",
      render: (v) => `${v}%`,
    },
    {
      title: "Plan says",
      key: "mismatch",
      width: 150,
      render: (_, g) => {
        const m = g.mismatch;
        if (!m) return <span style={{ color: "#bbb" }}>—</span>;
        return m.flagged
          ? <Tag color="orange" title={`The plan's typed progress averages ${m.typed}%; the field counts ${m.counted}%`}>{m.typed}% ({m.gap > 0 ? "+" : ""}{m.gap})</Tag>
          : <span style={{ color: "#888" }}>{m.typed}%</span>;
      },
    },
  ];

  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={<span>Field progress <span style={{ color: "#888", fontWeight: 400 }}>· construction tracking, counted from model elements</span></span>}
      extra={
        <Segmented
          size="small"
          value={by}
          onChange={setBy}
          options={[{ label: "By package", value: "package" }, { label: "By area", value: "area" }]}
        />
      }
    >
      {err ? <Alert type="error" showIcon message={err} /> : !data ? <Skeleton active paragraph={{ rows: 3 }} /> : (
        <>
          {measured.length ? (
            <Table size="small" rowKey="key" pagination={false} dataSource={measured} columns={columns} />
          ) : (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
              No field work is measured yet. A field activity is measured once its model elements are linked:
              open its Field cell on the schedule and pick the elements it installs.
            </Typography.Paragraph>
          )}
          {unmeasured.length ? (
            <div style={{ marginTop: 10 }}>
              <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setShowUnmeasured((v) => !v)}>
                {showUnmeasured ? "Hide" : "Show"} {unmeasured.length} {by === "area" ? "area" : "package"}{unmeasured.length === 1 ? "" : "s"} not measured yet
              </Button>
              {showUnmeasured ? (
                <div style={{ marginTop: 6, color: "#666", fontSize: 12 }}>
                  {unmeasured.map((g) => (
                    <Tag key={g.key} style={{ marginBottom: 4 }}>
                      {g.label} · {g.activities} activit{g.activities === 1 ? "y" : "ies"}
                    </Tag>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
